import { Injectable, DOCUMENT, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { DeepPartial } from '../interfaces/deep-partial.type';
import { mergeDeep } from '../utils/merge-deep';
import { LayoutService } from './layout.service';
import { configs } from './configs';
import { ConfigName } from '../interfaces/config-name.model';
import { Config } from '../interfaces/config.model';
import { ColorVariable } from '../components/config-panel/color-variables';
import { ConfigService as ConfigConfigService } from './../config/config.service';

@Injectable({
	providedIn: 'root',
})
export class ConfigService {
	private document = inject<Document>(DOCUMENT);
	private configService = inject(ConfigConfigService);
	private layoutService = inject(LayoutService);

	defaultConfig = ConfigName.apollo;

	configs: Config[] = configs;

	private _configSubject = new BehaviorSubject(this.configs.find((c) => c.id === this.defaultConfig));
	config$ = this._configSubject.asObservable();

	constructor() {
		this.config$.subscribe((config) => this._updateConfig(config));
	}

	setConfig(config: ConfigName) {
		const settings = this.configs.find((c) => c.id === config);
		if (settings) {
			this._configSubject.next(settings);
		}
	}

	changeImage(logoBlob: string) {
		this.configService.changeImage(logoBlob);
	}

	changeFavIcon(favIconBlob: string) {
		this.configService.changeFavIcon(favIconBlob);
	}

	updateConfig(config: DeepPartial<Config>) {
		this._configSubject.next(mergeDeep({ ...this._configSubject.getValue() }, config));
	}

	private _updateConfig(config: Config) {
		const body = this.document.body;

		this.configs.forEach((c) => {
			if (body.classList.contains(c.id)) {
				body.classList.remove(c.id);
			}
		});

		body.classList.add(config.id);

		config.sidenav.state === 'expanded' ? this.layoutService.expandSidenav() : this.layoutService.collapseSidenav();

		this.document.body.dir = config.rtl ? 'rtl' : 'ltr';

		// Workaround so charts and other externals know they have to resize on Layout switch
		if (window) {
			window.dispatchEvent(new Event('resize'));

			setTimeout(() => {
				window.dispatchEvent(new Event('resize'));
			}, 200);
		}
	}

	changeColorByCustomer(color: ColorVariable) {
		if (this.document) {
			this.document.documentElement.style.setProperty('--color-primary', color.default.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-primary-rgb', color.default);
			this.document.documentElement.style.setProperty('--color-primary-contrast', color.contrast.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-primary-contrast-rgb', color.contrast);

			this.document.body.style.setProperty('--color-primary-rgb', color.default);
			this.document.body.style.setProperty('--color-primary-contrast', color.contrast.replace('rgb(', '').replace(')', ''));
			this.document.body.style.setProperty('--color-primary-contrast-rgb', color.contrast);

			this.document.documentElement.style.setProperty('--color-accent', color.accent.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-warn', color.warn.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-primary-contrast', color.contrast.replace('rgb(', '').replace(')', ''));
		}
	}
}
